import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bulkAssignAgencyPreset, bulkAssignSellerPreset, fetchAgency, fetchSeller } from "~/modules/Clients/api";
import { useMessage } from "~/components/Dialogs";
import { Progress } from "~/components/Progress";
import { Intent } from "~/lib/intent";
import { renderToggle } from "~/lib/helpers";
import { IPreset, PresetSide } from "~/modules/Dictionary/models/Preset";
import { groupRillFieldIds } from "~/modules/Dictionary/models/RillFieldCatalog";
import { usePresetsQuery } from "~/modules/Dictionary/queries";
import { IUserPermission, PermissionResourceType } from "~/modules/Users/models/UserPermission";
import style from "./UserPermissionPresets.module.scss";

const getErrorMessage = (error: any): string => error?.response?.data?.message || error?.message || "Unknown error";

interface IPermissionPresetPanelProps {
  permission: IUserPermission;
}

const PermissionPresetPanel: React.FC<IPermissionPresetPanelProps> = ({ permission }) => {
  const queryClient = useQueryClient();
  const message = useMessage();
  const isAgency = permission.resourceType === PermissionResourceType.AGENCY;
  const side: PresetSide = isAgency ? "demand" : "supply";
  const resourceId = permission.resourceId;

  const { data: agency, isPending: isAgencyPending } = useQuery({
    queryKey: ["user-permission-agency", resourceId],
    queryFn: () => fetchAgency(resourceId),
    enabled: isAgency,
  });
  const { data: seller, isPending: isSellerPending } = useQuery({
    queryKey: ["user-permission-seller", resourceId],
    queryFn: () => fetchSeller(resourceId),
    enabled: !isAgency,
  });

  const { data: presets = [], isPending: isPresetsPending } = usePresetsQuery();

  const assignMutation = useMutation({
    mutationFn: (presetId: number) =>
      isAgency ? bulkAssignAgencyPreset([resourceId], presetId) : bulkAssignSellerPreset([resourceId], presetId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [isAgency ? "user-permission-agency" : "user-permission-seller", resourceId],
      }),
    onError: (e: any) =>
      message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> }),
  });

  const isPending = (isAgency ? isAgencyPending : isSellerPending) || isPresetsPending;
  const resourceName = isAgency ? agency?.title : seller?.name;
  const currentPresetId = isAgency ? agency?.presetId ?? null : seller?.presetId ?? null;
  const sidePresets = presets.filter((p) => p.side === side);

  const handleToggle = (preset: IPreset) => {
    if (currentPresetId !== preset.id) {
      assignMutation.mutate(preset.id);
    }
  };

  if (isPending) {
    return <Progress small />;
  }

  return (
    <div className={style.panel}>
      <div className={style.resourceTitle}>
        {isAgency ? "Buyer" : "Seller"}: {resourceName ?? `#${resourceId}`}
      </div>
      {sidePresets.length === 0 && <div className={style.empty}>No presets configured for this side yet.</div>}
      <div className={style.blocks}>
        {sidePresets.map((preset) => {
          const fieldGroups = groupRillFieldIds(preset.fieldIds);
          return (
            <div key={preset.id} className={style.block}>
              <div className={style.blockHead}>
                <span className={[style.toggle, assignMutation.isPending ? style.disabled : ""].join(" ")}>
                  {renderToggle(currentPresetId === preset.id, () => handleToggle(preset))}
                </span>
                <span className={style.blockName}>
                  {preset.name} ({preset.fieldIds.length} fields)
                </span>
              </div>
              <div className={style.blockBody}>
                {fieldGroups.length === 0 && <div className={style.empty}>No field IDs</div>}
                {fieldGroups.map((group) => (
                  <div key={group.report} className={style.fieldGroup}>
                    <div className={style.groupLabel}>{group.report}</div>
                    {group.fields.map((field) => (
                      <div key={field} className={style.field}>
                        {field}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const UserPermissionPresets: React.FC<{ permissions?: IUserPermission[] }> = ({ permissions }) => {
  const relevant = (permissions ?? []).filter(
    (p) =>
      p.resourceType === PermissionResourceType.SELLER || p.resourceType === PermissionResourceType.AGENCY
  );

  if (relevant.length === 0) {
    return null;
  }

  return (
    <div>
      {relevant.map((permission) => (
        <PermissionPresetPanel key={`${permission.resourceType}-${permission.resourceId}`} permission={permission} />
      ))}
    </div>
  );
};
